import { ref, shallowRef, computed, onUnmounted } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import JsSIP from "jssip";
import Toast from "@/helpers/toast.helper";
import useRole from "@/composables/use-role";
import { getSipLineExtension } from "@/helpers/sip-line-extension";
import {
  MERAPI_SIP_DOMAIN,
  MERAPI_SIP_PASSWORD,
  MERAPI_SIP_RINGTONE_URL,
  MERAPI_SIP_WS_URI,
} from "@/config/merapi-sip";

const CALL_TIMEOUT_MS = 30000;

// Same uri+ws+password as the live UA → keep the UA registered across
// component remounts (CallDialog used to restart registration on every open).
let lastFingerprint = null;
const fingerprint = (uri, ws, pw) => `${uri}\0${ws}\0${pw}`;

const pad2 = (n) => String(n).padStart(2, "0");
const formatMMSS = (s) => `${pad2(Math.floor(s / 60))}:${pad2(s % 60)}`;

export function useJsSIP() {
  const queryClient = useQueryClient();
  const { isAdmin } = useRole();

  // ---------------- state ----------------
  const phone = shallowRef(null);
  const session = shallowRef(null);
  const callState = ref("idle"); // idle|ringing|connecting|connected|ended|rejected
  const callDirection = ref(null); // "outgoing" | "incoming"
  const callSource = ref(null); // "lead" (CallDialog) | "global" (dialpad)
  const callType = ref(null); // "default" | "whatsapp" (set in initPhone)
  const remoteNumber = ref("");
  const callDuration = ref(0);
  const isMuted = ref(false);
  const isOnHold = ref(false);
  const isRegistered = ref(false);
  const registeredSipUser = ref(null);
  const registeredSipAor = ref(null);
  const registrationError = ref(null);
  const lastCallEnded = ref(null);
  const sipRealm = ref(MERAPI_SIP_DOMAIN);
  /** Set by initPhone: business / CLI number for Merapi (WhatsApp line), sent on INVITE as `caller_number:` */
  const callerNumber = ref(null);
  /** Set by `config.unique_id` in initPhone; used as `X-Activity-Id` when `makeCall` has no second arg */
  const sipUniqueId = ref(null);

  let durationInterval = null;
  let callTimeout = null;
  let remoteAudio = null;
  let ringAudio = null;
  let ringtoneUrl = MERAPI_SIP_RINGTONE_URL;

  const formattedDuration = computed(() => {
    const s = callDuration.value;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${pad2(h)}:${pad2(m)}:${pad2(sec)}`
      : `${pad2(m)}:${pad2(sec)}`;
  });

  // ---------------- small helpers ----------------
  const suppressRegToast = () => {
    if (isAdmin.value) return true;
    return queryClient.getQueryData(["QUERY_GET_ROLE"])?.isAdmin === true;
  };

  const initAudio = () => {
    if (!remoteAudio) {
      remoteAudio = new window.Audio();
      remoteAudio.autoplay = true;
    }
  };

  const initRingAudio = () => {
    if (ringAudio && ringAudio.src && !ringAudio.src.endsWith(ringtoneUrl)) {
      // URL was reconfigured (e.g. second initPhone call). Rebuild.
      try {
        ringAudio.pause();
      } catch (e) {
        console.warn("ringAudio rebuild pause:", e);
      }
      ringAudio = null;
    }
    if (!ringAudio) {
      ringAudio = new window.Audio(ringtoneUrl);
      ringAudio.loop = true;
      ringAudio.preload = "auto";
    }
  };

  const startRinging = () => {
    initRingAudio();
    if (!ringAudio) return;
    try {
      ringAudio.currentTime = 0;
    } catch (e) {
      console.warn("ringAudio seek:", e);
    }
    // Browser autoplay policies may reject this if the tab has had no user
    // gesture yet — just log and move on; the visual InCallBar still shows.
    const p = ringAudio.play();
    if (p && typeof p.catch === "function") {
      p.catch((err) => console.warn("ringAudio autoplay blocked:", err));
    }
  };

  const stopRinging = () => {
    if (!ringAudio) return;
    try {
      ringAudio.pause();
      ringAudio.currentTime = 0;
    } catch (e) {
      console.warn("stopRinging:", e);
    }
  };

  const clearDurationTimer = () => {
    if (durationInterval) clearInterval(durationInterval);
    durationInterval = null;
  };

  const clearCallTimeout = () => {
    if (callTimeout) clearTimeout(callTimeout);
    callTimeout = null;
  };

  const startCallTimer = () => {
    clearCallTimeout();
    callDuration.value = 0;
    durationInterval = setInterval(() => {
      callDuration.value += 1;
    }, 1000);
  };

  const stopAudio = () => {
    if (remoteAudio) {
      remoteAudio.muted = false;
      remoteAudio.pause();
      remoteAudio.srcObject = null;
      remoteAudio.currentTime = 0;
    }
  };

  const stopMediaTracks = (sess) => {
    const pc = sess?.connection;
    if (!pc) return;
    try {
      pc.getSenders?.().forEach((s) => s.track?.stop());
      pc.getReceivers?.().forEach((r) => r.track?.stop());
    } catch (e) {
      console.warn("stopMediaTracks:", e);
    }
  };

  const safeTerminate = (sess) => {
    try {
      const ended = typeof sess?.isEnded === "function" && sess.isEnded();
      if (!ended) sess?.terminate();
    } catch (e) {
      console.warn("safeTerminate:", e);
    }
  };

  const rejectSession = (sess, status_code, reason_phrase) => {
    try {
      sess.terminate({ status_code, reason_phrase });
    } catch (e) {
      console.warn("rejectSession:", e);
    }
  };

  const captureRejectedCall = (ev) => {
    lastCallEnded.value = {
      number: remoteNumber.value,
      duration: "",
      wasConnected: false,
      wasGlobalCall: callSource.value === "global",
      endOrigin: "rejected",
      statusCode: ev?.message?.status_code,
      cause: ev?.cause || ev?.message?.cause,
    };
  };

  const startCallTimeout = () => {
    clearCallTimeout();
    callTimeout = setTimeout(() => {
      if (callState.value === "connecting") {
        callState.value = "rejected";
        captureRejectedCall();
        safeTerminate(session.value);
        setTimeout(resetSession, 100);
      }
    }, CALL_TIMEOUT_MS);
  };

  // ---------------- session lifecycle ----------------
  const handleNewSession = (newSession) => {
    // Merapi's WhatsApp gateway has a non-standard call flow:
    //   1. We send INVITE with call_type:whatsapp (the "trigger").
    //   2. PBX replies with 100 → 183 (early-media ringback) and stays in
    //      EARLY state forever — no 200 OK ever comes on this dialog.
    //   3. When the callee picks up WhatsApp on their phone, the PBX sends
    //      a *new* incoming INVITE back to our browser in a different
    //      dialog (different Call-ID, From = gateway bridge number).
    //   4. Answering that incoming INVITE is what establishes the real
    //      RTP media path for the WhatsApp call.
    //
    // So: if an incoming INVITE arrives while we have an active outgoing
    // WhatsApp call in connecting/ringing state, it's this bridge callback
    // and we must auto-answer it instead of treating it as a new inbound.
    let isWhatsappBridgeCallback = false;
    let isIncomingCall = false;
    let preservedRemoteNumber = "";
    let preservedCallSource = null;

    if (newSession?.direction === "incoming") {
      isWhatsappBridgeCallback =
        callType.value === "whatsapp" &&
        callDirection.value === "outgoing" &&
        ["connecting", "ringing"].includes(callState.value);

      if (isWhatsappBridgeCallback) {
        console.log(
          "[useJsSIP] WhatsApp gateway bridge INVITE — auto-answering"
        );
        try {
          newSession.answer({
            mediaConstraints: { audio: true, video: false },
          });
        } catch (e) {
          console.error("WhatsApp bridge auto-answer failed:", e);
          rejectSession(newSession, 486, "Auto-answer failed");
          return;
        }

        // Preserve the user-visible context from the outbound trigger call.
        preservedRemoteNumber = remoteNumber.value;
        preservedCallSource = callSource.value;

        // Silently retire the outbound trigger session: strip its listeners
        // first so its CANCEL/487 teardown does not flip our state back to
        // "rejected", then terminate so the PBX cleans up the EARLY dialog.
        const outbound = session.value;
        if (outbound && outbound !== newSession) {
          try {
            outbound.removeAllListeners?.();
          } catch (err) {
            console.warn("removeAllListeners(outbound):", err);
          }
          safeTerminate(outbound);
          session.value = null;
        }
      } else {
        // Genuine inbound call. Reject with 486 if we're already on another
        // call; otherwise let it ring through to the UI.
        if (session.value) {
          console.log("[useJsSIP] Incoming INVITE while busy — 486");
          rejectSession(newSession, 486, "Busy Here");
          return;
        }
        isIncomingCall = true;
        console.log("[useJsSIP] Incoming INVITE — ringing");
      }
    }

    // Only tear down the previous session when we're actually taking over
    // (e.g. stale outgoing session lingering). Never for incoming while busy.
    if (session.value && session.value !== newSession) {
      safeTerminate(session.value);
    }

    session.value = newSession;

    if (isWhatsappBridgeCallback) {
      // Still conceptually our outbound WhatsApp call — the SIP-level
      // "incoming" is just the gateway's media-path callback. Keep the
      // original dialed number / source so the UI does not flip.
      callDirection.value = "outgoing";
      remoteNumber.value = preservedRemoteNumber;
      callSource.value = preservedCallSource;
    } else {
      callDirection.value = newSession.direction;
      remoteNumber.value = newSession.remote_identity?.uri?.user || "Unknown";
      if (isIncomingCall) {
        // Inbound calls surface through the global InCallBar UI.
        callSource.value = "global";
      }
    }

    const failAsRejected = (ev) => {
      callState.value = "rejected";
      captureRejectedCall(ev);
      clearCallTimeout();
      stopRinging();
      safeTerminate(session.value);
      resetSession();
    };

    session.value.on("ended", (ev) => {
      console.log("[SIP] ended", ev?.cause);
      handleCallEnd(ev);
    });
    session.value.on("failed", (ev) => {
      const status = ev?.message?.status_code;
      const reason = ev?.message?.reason_phrase || ev?.cause;
      console.log("[SIP] failed", ev?.cause, status);

      // 403 from the PBX means the gateway refused the call (wrong permissions,
      // wrong call_type, duplicate X-Activity-Id, etc.) — surface that clearly so
      // the agent doesn't sit on "connecting" for 15s waiting for the reject.
      if (status === 403) {
        const reasonText = reason || "Forbidden";
        Toast.error(
          `Call rejected by PBX (403 ${reasonText}). Check extension permissions for this call type.`
        );
      }

      failAsRejected(ev);
    });
    session.value.on("rejected", (ev) => {
      console.log("[SIP] rejected", ev?.cause, ev?.message?.status_code);
      failAsRejected(ev);
    });
    session.value.on("cancel", () => {
      console.log("[SIP] cancel");
      failAsRejected({ cause: "Canceled" });
    });
    session.value.on("progress", (ev) => {
      // 180/183 means the far end is ringing — not yet picked up. Just log;
      // do not flip UI to "connected" here, otherwise the button flashes
      // connected while the phone is still ringing and early-media ringback
      // keeps playing because the stream has not switched over yet.
      console.log("[SIP] progress", ev?.response?.status_code);
    });

    // Match commit a0bfd101 outbound flow:
    //   accepted  (200 OK)  → "connecting" (peer answered, awaiting ACK)
    //   confirmed (ACK)     → "connected" + start timer + DTMF
    // Guard: do not downgrade state if the WhatsApp bridge auto-answer has
    // already flipped us to "connected" in handleNewSession above.
    session.value.on("accepted", () => {
      console.log("[SIP] accepted");
      if (callState.value !== "connected") {
        callState.value = "connecting";
      }
    });

    session.value.on("confirmed", () => {
      console.log("[SIP] confirmed");
      callState.value = "connected";
      startCallTimer();
      try {
        const localStream = session.value?.connection?.getLocalStreams?.()[0];
        const track = localStream?.getAudioTracks?.()[0];
        if (track) {
          const dtmf = session.value.connection.createDTMFSender(track);
          session.value.sendDTMF = (tone) => dtmf.insertDTMF(tone);
        }
      } catch (e) {
        console.warn("DTMF setup skipped:", e);
      }
    });

    session.value.on("peerconnection", (e) => {
      const pc = e.peerconnection;

      pc.onaddstream = (ev) => {
        console.log("[SIP] addstream (via pc.onaddstream)");
        if (remoteAudio) {
          remoteAudio.srcObject = ev.stream;
          remoteAudio.play();
        }
      };

      // Fallback when remote hangs up: some trunks tear down RTP/ICE without
      // emitting RTCSession "ended". Defer a tick so the real "ended" runs first.
      pc.addEventListener("connectionstatechange", () => {
        console.log("[SIP] connectionState:", pc.connectionState);
        const cs = pc.connectionState;
        if (cs !== "closed" && cs !== "failed") return;
        setTimeout(() => {
          if (!["ringing", "connecting", "connected"].includes(callState.value))
            return;
          handleCallEnd({
            cause:
              cs === "failed"
                ? "WebRTC connection failed"
                : "WebRTC connection closed",
            _fromPeerConnection: true,
          });
        }, 0);
      });
    });

    if (isWhatsappBridgeCallback) {
      // WhatsApp call is already connected on the callee's side; flip the UI
      // straight to connected. `accepted` on the new session will fire soon
      // after and `markConnected` is idempotent, so DTMF gets wired up then.
      clearCallTimeout();
      callState.value = "connected";
      startCallTimer();
    } else if (isIncomingCall) {
      // Genuine inbound: wait in "ringing" until the user calls answerCall()
      // or endCall(). No call-timeout here — the caller decides when to give
      // up. The peerconnection is created by JsSIP at answer() time, so the
      // addstream listener attaches inside answerCall().
      callState.value = "ringing";
      startRinging();
    } else {
      callState.value = "connecting";
      startCallTimeout();
      // Only play remote audio here — state transitions happen on SIP events
      // (accepted/confirmed) so early media / ringback does not look like "connected".
      session.value.connection.addEventListener("addstream", (e) => {
        if (remoteAudio) remoteAudio.srcObject = e.stream;
      });
    }
  };

  const handleCallEnd = (ev) => {
    if (["rejected", "ended", "idle"].includes(callState.value)) return;

    const origin = ev?.originator;
    let endOrigin = "unknown";
    if (origin === "remote") endOrigin = "remote";
    else if (origin === "local") endOrigin = "local";
    else if (ev?._fromPeerConnection) endOrigin = "peer_closed";

    lastCallEnded.value = {
      number: remoteNumber.value,
      duration: formatMMSS(callDuration.value),
      wasConnected: callState.value === "connected",
      wasGlobalCall: callSource.value === "global",
      endOrigin,
      cause: ev?.cause,
    };
    callState.value = "ended";

    clearCallTimeout();
    clearDurationTimer();
    stopRinging();
    stopAudio();
    if (session.value) {
      stopMediaTracks(session.value);
      safeTerminate(session.value);
      session.value = null;
    }
    setTimeout(resetSession, 0);
  };

  const resetSession = () => {
    if (session.value) {
      safeTerminate(session.value);
      session.value = null;
    }
    // Preserve "rejected" / "ended" so UI can still show the result.
    if (callState.value !== "rejected" && callState.value !== "ended") {
      callState.value = "idle";
    }
    callDirection.value = null;
    remoteNumber.value = "";
    callDuration.value = 0;
    isMuted.value = false;
    isOnHold.value = false;
    callSource.value = null;
    clearCallTimeout();
    clearDurationTimer();
    stopRinging();
    stopAudio();
  };

  // ---------------- JsSIP init / registration ----------------
  const initPhone = (config) => {
    if (!config.uri || !config.password) {
      console.error("SIP URI and password are required");
      return false;
    }
    if (!JsSIP?.UA) {
      console.error("JsSIP library not loaded");
      return false;
    }

    const rawCt =
      config.call_type != null ? String(config.call_type).trim() : "";
    const ct = rawCt || "default";

    let regUri = String(config.uri).trim();
    if (!/^sip:/i.test(regUri)) regUri = `sip:${regUri}`;
    const wsUri = config.wsUri || MERAPI_SIP_WS_URI;
    const password = String(config.password);
    const fp = fingerprint(regUri, wsUri, password);

    // Optional per-init ringtone override. Falls back to the shared default.
    if (config.ringtoneUrl && typeof config.ringtoneUrl === "string") {
      ringtoneUrl = config.ringtoneUrl;
    } else {
      ringtoneUrl = MERAPI_SIP_RINGTONE_URL;
    }

    // Optional caller CLI / business WhatsApp line (Merapi `caller_number:` on INVITE).
    const rawCaller = config.caller_number;
    if (rawCaller != null && String(rawCaller).trim() !== "") {
      callerNumber.value = String(rawCaller).trim();
    } else {
      callerNumber.value = null;
    }

    const rawUid = config.unique_id;
    if (rawUid != null && String(rawUid).trim() !== "") {
      sipUniqueId.value = String(rawUid).trim();
    } else {
      sipUniqueId.value = null;
    }

    // Same line/transport/secret: keep UA, just update call_type + caller_number + unique_id for next INVITEs.
    if (phone.value && lastFingerprint === fp) {
      callType.value = ct;
      return true;
    }

    if (phone.value) {
      try {
        phone.value.stop();
      } catch (e) {
        console.warn("stop UA:", e);
      }
      phone.value = null;
    }
    lastFingerprint = null;
    isRegistered.value = false;
    registrationError.value = null;
    registeredSipUser.value = null;
    registeredSipAor.value = null;
    callType.value = ct;

    try {
      const socket = new JsSIP.WebSocketInterface(wsUri);
      const at = regUri.lastIndexOf("@");
      if (at !== -1) sipRealm.value = regUri.slice(at + 1).split(";")[0];

      if (import.meta.env.DEV) JsSIP.debug.enable("JsSIP:*");

      // session_timers: false matches the working WA test page (waob-call.html).
      phone.value = new JsSIP.UA({
        sockets: [socket],
        uri: regUri,
        password,
        username: String(config.username),
        register: true,
        session_timers: false,
      });
      lastFingerprint = fp;

      phone.value.on("registered", () => {
        isRegistered.value = true;
        registrationError.value = null;
        const userPart = regUri.replace(/^sip:/i, "").split("@")[0] || null;
        registeredSipUser.value = userPart;
        registeredSipAor.value = regUri;
      });

      phone.value.on("unregistered", () => {
        isRegistered.value = false;
        registeredSipUser.value = null;
        registeredSipAor.value = null;
      });

      phone.value.on("registrationFailed", (ev) => {
        isRegistered.value = false;
        lastFingerprint = null;
        registeredSipUser.value = null;
        registeredSipAor.value = null;
        const code = ev?.response?.status_code;
        const cause = ev?.cause ?? "Unknown";
        const detail = code != null ? `SIP ${code} — ${cause}` : String(cause);
        registrationError.value = `Phone not registered (${detail}). Outbound calls will not work from this browser.`;
        console.error("SIP registration failed:", cause, code ?? "");
        if (!suppressRegToast()) Toast.error(registrationError.value);
      });

      phone.value.on("newRTCSession", (ev) => handleNewSession(ev.session));

      phone.value.start();
      initAudio();
      return true;
    } catch (e) {
      console.error("Failed to initialize JsSIP:", e);
      lastFingerprint = null;
      return false;
    }
  };

  // Standby REGISTER as the default trunk (e.g. after CallDialog closes).
  // Idempotent if the UA is already on the same fingerprint.
  const resumeStandbySipRegistration = () => {
    const id = getSipLineExtension();
    if (!id) {
      registrationError.value =
        "No SIP line in your profile (set agent_id or log in with a user id).";
      return false;
    }
    return initPhone({
      wsUri: MERAPI_SIP_WS_URI,
      uri: `${id}@${MERAPI_SIP_DOMAIN}`,
      password: MERAPI_SIP_PASSWORD,
      username: id,
      call_type: "default",
    });
  };

  // ---------------- call actions ----------------
  const makeCall = (phoneNumber, activityId = null) => {
    const effectiveActivityId =
      activityId != null && String(activityId).trim() !== ""
        ? String(activityId).trim()
        : sipUniqueId.value;
    callSource.value = effectiveActivityId ? "lead" : "global";

    if (!phone.value) {
      console.error("Phone not initialized");
      return false;
    }
    if (!isRegistered.value) {
      console.error("Phone not registered");
      return false;
    }
    if (!phoneNumber) {
      console.error("Phone number is required");
      return false;
    }

    try {
      // Request-URI = sip:number@realm. 0 → 62. WhatsApp: digits-only user part.
      let dialUser = String(phoneNumber).trim();
      if (dialUser.startsWith("0")) dialUser = "62" + dialUser.slice(1);
      if (callType.value === "whatsapp") dialUser = dialUser.replace(/\D/g, "");
      if (callType.value === "whatsapp" && !dialUser) {
        console.error("Phone number is required for WhatsApp call");
        return false;
      }

      const ct = callType.value || "default";
      const extraHeaders = [
        ...(effectiveActivityId
          ? [`X-Activity-Id: ${effectiveActivityId}`]
          : []),
        `call_type: ${ct}`,
        "X-Source: CRM-WebRTC",
        ...(ct === "whatsapp" && callerNumber.value
          ? [`caller_number: ${String(callerNumber.value).trim()}`]
          : []),
      ];

      // Match commit a0bfd101 outbound call options.
      phone.value.call(`sip:${dialUser}@${sipRealm.value}`, {
        mediaConstraints: { audio: true, video: false },
        extraHeaders,
        rtcOfferConstraints: {
          offerToReceiveAudio: 1,
          offerToReceiveVideo: 0,
        },
      });
      return true;
    } catch (e) {
      console.error("Failed to make call:", e);
      return false;
    }
  };

  const answerCall = () => {
    const sess = session.value;
    if (!sess || sess.direction !== "incoming") return false;
    if (callState.value !== "ringing") return false;
    try {
      sess.answer({
        mediaConstraints: { audio: true, video: false },
        rtcOfferConstraints: {
          offerToReceiveAudio: 1,
          offerToReceiveVideo: 0,
        },
      });
      stopRinging();
      // Attach audio as soon as the peer connection is available. jssip fires
      // "peerconnection" at answer() time, but covering both paths is cheap.
      try {
        sess.connection?.addEventListener?.("addstream", (e) => {
          if (remoteAudio) remoteAudio.srcObject = e.stream;
        });
      } catch (e) {
        console.warn("answerCall addstream wire-up:", e);
      }
      callState.value = "connecting";
      return true;
    } catch (e) {
      console.error("Failed to answer call:", e);
      rejectSession(sess, 500, "Answer Failed");
      return false;
    }
  };

  /**
   * Blind (unattended) SIP transfer. Sends a REFER to the PBX and tears down
   * our leg — the PBX is then responsible for connecting the target to the
   * original caller. Must be called while the call is `connected`.
   * Not supported when `callDirection` is `incoming` (genuine inbound). The
   * WhatsApp bridge leg is still `callDirection: "outgoing"` so transfer stays
   * available there.
   *
   * `target` accepts a bare number (e.g. "310010", "081234…") or a full SIP
   * URI ("sip:310010@merapi.latera.id"). Bare numbers are normalised the
   * same way `makeCall` does (leading `0` → `62`, digits-only for WhatsApp),
   * then prefixed with `sip:...@<sipRealm>`.
   */
  const transferCall = (target) => {
    const sess = session.value;
    if (!sess) {
      console.error("transferCall: no active session");
      return false;
    }
    if (callDirection.value === "incoming") {
      console.warn("transferCall: not supported for inbound calls");
      return false;
    }
    if (callState.value !== "connected") {
      console.error("transferCall: call must be connected first");
      return false;
    }
    const raw = target == null ? "" : String(target).trim();
    if (!raw) {
      console.error("transferCall: target is required");
      return false;
    }

    let referTarget;
    if (/^sip:/i.test(raw)) {
      referTarget = raw;
    } else {
      let dialUser = raw;
      if (dialUser.startsWith("0")) dialUser = "62" + dialUser.slice(1);
      if (callType.value === "whatsapp") dialUser = dialUser.replace(/\D/g, "");
      if (!dialUser) {
        console.error("transferCall: invalid target");
        return false;
      }
      referTarget = `sip:${dialUser}@${sipRealm.value}`;
    }

    try {
      sess.refer(referTarget);
      // Blind transfer: tear down our leg immediately after REFER, matching
      // the Latera reference demo. The PBX handles ringing the target and
      // bridging once they answer.
      safeTerminate(sess);
      setTimeout(resetSession, 100);
      return true;
    } catch (e) {
      console.error("transferCall:", e);
      return false;
    }
  };

  const endCall = () => {
    const sess = session.value;
    // Declining a ringing inbound call must send a 603 so the caller knows
    // the callee actively rejected rather than timed out.
    if (
      sess &&
      sess.direction === "incoming" &&
      callState.value === "ringing"
    ) {
      rejectSession(sess, 603, "Decline");
    } else {
      safeTerminate(sess);
    }
    setTimeout(resetSession, 100);
  };

  const toggleMute = () => {
    if (!session.value || callState.value !== "connected") return;
    const muted = session.value.isMuted().audio;
    if (muted) session.value.unmute({ audio: true });
    else session.value.mute({ audio: true });
    isMuted.value = !muted;
  };

  const toggleHold = () => {
    if (!session.value || callState.value !== "connected") return;
    try {
      if (isOnHold.value) session.value.unhold();
      else session.value.hold();
      isOnHold.value = !isOnHold.value;
    } catch (e) {
      console.warn("toggleHold:", e);
    }
  };

  const sendDTMF = (tone) => session.value?.sendDTMF?.(tone);

  const clearLastCallEnded = () => {
    lastCallEnded.value = null;
    if (callState.value === "ended" || callState.value === "rejected") {
      callState.value = "idle";
    }
  };

  // Full teardown on logout so UI does not keep stale state across sessions.
  const shutdownSip = () => {
    clearCallTimeout();
    clearDurationTimer();
    stopRinging();
    stopAudio();
    if (session.value) {
      safeTerminate(session.value);
      session.value = null;
    }
    if (phone.value) {
      try {
        phone.value.stop();
      } catch (e) {
        console.warn("stop UA:", e);
      }
      phone.value = null;
    }
    lastFingerprint = null;
    callState.value = "idle";
    callDirection.value = null;
    remoteNumber.value = "";
    callDuration.value = 0;
    isMuted.value = false;
    isOnHold.value = false;
    isRegistered.value = false;
    registeredSipUser.value = null;
    registeredSipAor.value = null;
    registrationError.value = null;
    lastCallEnded.value = null;
    callSource.value = null;
    callType.value = null;
    callerNumber.value = null;
    sipUniqueId.value = null;
    sipRealm.value = MERAPI_SIP_DOMAIN;
  };

  onUnmounted(() => {
    safeTerminate(session.value);
    if (phone.value) {
      try {
        phone.value.stop();
      } catch (e) {
        console.warn("stop UA:", e);
      }
    }
    clearDurationTimer();
    clearCallTimeout();
    stopRinging();
  });

  return {
    // state
    phone,
    session,
    callState,
    callDirection,
    remoteNumber,
    callDuration,
    isMuted,
    isOnHold,
    isRegistered,
    registeredSipUser,
    registeredSipAor,
    registrationError,
    lastCallEnded,
    callSource,
    callType,
    callerNumber,
    sipUniqueId,
    formattedDuration,
    // actions
    initPhone,
    makeCall,
    answerCall,
    endCall,
    transferCall,
    toggleMute,
    toggleHold,
    sendDTMF,
    clearLastCallEnded,
    resumeStandbySipRegistration,
    shutdownSip,
  };
}
